/** Thin styled wrappers over Radix primitives. */
import * as RD from '@radix-ui/react-dialog';
import * as RM from '@radix-ui/react-dropdown-menu';
import * as RP from '@radix-ui/react-popover';
import * as RT from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const TooltipProvider = RT.Provider;

export function Tip({ content, children, side = 'top', align = 'center', delay }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right'; align?: 'start' | 'center' | 'end'; delay?: number }) {
  if (!content) return <>{children}</>;
  return (
    <RT.Root {...(delay !== undefined ? { delayDuration: delay } : {})}>
      <RT.Trigger asChild>{children}</RT.Trigger>
      <RT.Portal>
        <RT.Content
          side={side}
          align={align}
          sideOffset={6}
          className="z-50 max-w-xs animate-fade-in rounded-md border border-line-2 bg-panel-2 px-2.5 py-1.5 text-xs text-fg-2 shadow-xl shadow-black/40"
        >
          {content}
        </RT.Content>
      </RT.Portal>
    </RT.Root>
  );
}

export const Dialog = RD.Root;
export const DialogTrigger = RD.Trigger;
export const DialogClose = RD.Close;

export function DialogContent({
  title,
  description,
  children,
  className,
  hideClose,
  ...rest
}: { title: ReactNode; description?: ReactNode; children: ReactNode; className?: string; hideClose?: boolean } & Omit<ComponentPropsWithoutRef<typeof RD.Content>, 'title'>) {
  return (
    <RD.Portal>
      <RD.Overlay className="fixed inset-0 z-40 animate-fade-in bg-black/65 backdrop-blur-[2px]" />
      <RD.Content
        {...rest}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 max-h-[90dvh] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 animate-pop-in overflow-y-auto rounded-xl border border-line-2 bg-panel p-5 shadow-2xl shadow-black/60',
          className,
        )}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <RD.Title className="text-base font-semibold text-fg">{title}</RD.Title>
            {description ? <RD.Description className="mt-1 text-xs text-fg-3">{description}</RD.Description> : <RD.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</RD.Description>}
          </div>
          {!hideClose && (
            <RD.Close className="-mt-1 -mr-1 rounded-md p-1.5 text-fg-3 hover:bg-hover hover:text-fg" aria-label="Close">
              <X className="size-4" />
            </RD.Close>
          )}
        </div>
        {children}
      </RD.Content>
    </RD.Portal>
  );
}

export const Popover = RP.Root;
export const PopoverTrigger = RP.Trigger;
export const PopoverClose = RP.Close;
export const PopoverAnchor = RP.Anchor;

export function PopoverContent({ className, children, align = 'start', sideOffset = 6, ...rest }: ComponentPropsWithoutRef<typeof RP.Content>) {
  return (
    <RP.Portal>
      <RP.Content
        align={align}
        sideOffset={sideOffset}
        className={cn('z-50 animate-pop-in rounded-lg border border-line-2 bg-panel p-3 shadow-2xl shadow-black/50 outline-none', className)}
        {...rest}
      >
        {children}
      </RP.Content>
    </RP.Portal>
  );
}

export const Menu = RM.Root;
export const MenuTrigger = RM.Trigger;

export function MenuContent({ className, children, align = 'end', ...rest }: ComponentPropsWithoutRef<typeof RM.Content>) {
  return (
    <RM.Portal>
      <RM.Content
        align={align}
        sideOffset={6}
        className={cn('z-50 min-w-[200px] animate-pop-in rounded-lg border border-line-2 bg-panel p-1 shadow-2xl shadow-black/50', className)}
        {...rest}
      >
        {children}
      </RM.Content>
    </RM.Portal>
  );
}

export function MenuItem({ className, ...rest }: ComponentPropsWithoutRef<typeof RM.Item>) {
  return (
    <RM.Item
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-fg-2 outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-hover data-[highlighted]:text-fg',
        className,
      )}
      {...rest}
    />
  );
}

export const MenuSeparator = () => <RM.Separator className="my-1 h-px bg-line" />;
export const MenuLabel = ({ children }: { children: ReactNode }) => <RM.Label className="px-2.5 pt-1.5 pb-1 text-2xs font-medium tracking-wide text-fg-4 uppercase">{children}</RM.Label>;
